import React, {
  forwardRef,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import {
  Row,
  Col,
  Button,
  Modal,
  Form,
  Table,
  Space,
  Typography,
  Tooltip,
} from "antd";
import { FormattedMessage } from "react-intl";
import Surface from "../../../components/layouts/Surface";
import FormHeader from "../../../components/forms/FormHeader";
import { ReactComponent as ChildImg } from "../../../assets/no-child-info.svg";
import { InputItem, DatePickerItem } from "../../../components/forms/FormItems";
import AnimationChildWrapper from "../../../components/animations/AnimationChildWrapper";
import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import moment from "moment";
import PeriodsCalenderPicker from "../../../components/forms/PeriodsCalenderPicker";
import {
  disableStartDates as disableStartDatesFunc,
  disableEndDates as disableEndDatesFunc,
} from "../../../components/util/validationHelpers";

const { Text } = Typography;

/**
 * Memoized component that renders the form fields for non-available hours
 * Used inside the calendar picker to collect information about availability periods
 *
 * @component
 * @returns {React.ReactElement} The input fields for place and description
 */
const CalendarEntryComponent = React.memo(() => (
  <div>
    <AnimationChildWrapper key={2} itemKey={2}>
      <InputItem name={"place"} category={"availability"} required={true} />
    </AnimationChildWrapper>
    <AnimationChildWrapper key={3} itemKey={32}>
      <InputItem name={"description"} category={"availability"} />
    </AnimationChildWrapper>
  </div>
));

/**
 * Component that manages the table of availability periods and related operations
 *
 * @component
 * @param {Object} props - The component props
 * @param {Array} props.value - The current value of periods
 * @param {Function} props.onChange - Callback function when periods are changed
 * @returns {React.ReactElement} The periods table with add/edit/delete functionality
 */
const PeriodsTable = ({ value = [], onChange }) => {
  // Local state to track periods - separate from form values
  const [localPeriods, setLocalPeriods] = useState(value);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [periodModalVisible, setPeriodModalVisible] = useState(false);
  const [modalForm] = Form.useForm();

  // State for temporary hours during editing
  const [tempHours, setTempHours] = useState([]);

  // Update local periods when value changes from outside
  useEffect(() => {
    setLocalPeriods(value);
  }, [value]);

  // Monitor selectedPeriod changes and initialize tempHours
  useEffect(() => {
    if (selectedPeriod) {
      // Ensure non_available_hours is always an array
      if (
        !selectedPeriod.non_available_hours ||
        !Array.isArray(selectedPeriod.non_available_hours)
      ) {
        setSelectedPeriod((prev) => ({
          ...prev,
          non_available_hours: [],
        }));
      }

      // Initialize tempHours with the selected period's hours
      setTempHours(
        Array.isArray(selectedPeriod.non_available_hours)
          ? [...selectedPeriod.non_available_hours]
          : []
      );
    }
  }, [selectedPeriod]);

  /**
   * Calculates the number of unique days in a period's hours
   *
   * @param {Array} hours - Array of hour objects containing day_of_week
   * @returns {number} The count of unique days
   */
  const calculateDaysCount = useCallback((hours) => {
    if (!Array.isArray(hours) || hours.length === 0) return 0;
    const uniqueDays = new Set(hours.map((hour) => hour.day_of_week));
    return uniqueDays.size;
  }, []);

  /**
   * Calculates the total hours from minutes in a period
   *
   * @param {Array} hours - Array of hour objects containing duration_minutes
   * @returns {number} The total hours rounded to one decimal place
   */
  const calculateTotalHours = useCallback((hours) => {
    if (!Array.isArray(hours) || hours.length === 0) return 0;
    const totalMinutes = hours.reduce(
      (sum, hour) => sum + (hour.duration_minutes || 0),
      0
    );
    return Math.round(totalMinutes / 6) / 10;
  }, []);

  /**
   * Opens the modal to add a new period
   */
  const handleAddPeriod = useCallback(() => {
    modalForm.resetFields();
    setSelectedPeriod({
      id: Date.now(),
      non_available_hours: [],
    });
    setTempHours([]);
    setPeriodModalVisible(true);
  }, [modalForm]);

  /**
   * Opens the modal to edit an existing period
   *
   * @param {Object} period - The period object to edit
   */
  const handleEditPeriod = useCallback(
    (period) => {
      // Create a shallow copy of the period
      const periodCopy = { ...period };

      // Deep copy non_available_hours only if it exists and needs to be an array
      if (
        !periodCopy.non_available_hours ||
        !Array.isArray(periodCopy.non_available_hours)
      ) {
        periodCopy.non_available_hours = periodCopy.non_available_hours
          ? [...periodCopy.non_available_hours]
          : [];
      }

      setSelectedPeriod(periodCopy);
      setTempHours([...periodCopy.non_available_hours]);

      modalForm.setFieldsValue({
        effective_from: periodCopy.effective_from
          ? moment(periodCopy.effective_from)
          : null,
        effective_to: periodCopy.effective_to
          ? moment(periodCopy.effective_to)
          : null,
      });
      setPeriodModalVisible(true);
    },
    [modalForm]
  );

  /**
   * Deletes a period from the list
   *
   * @param {number|string} periodId - The ID of the period to delete
   */
  const handleDeletePeriod = useCallback(
    (periodId) => {
      const updatedPeriods = localPeriods.filter((p) => p.id !== periodId);
      setLocalPeriods(updatedPeriods);
      onChange(updatedPeriods); // Update the form value
    },
    [localPeriods, onChange]
  );

  /**
   * Closes the period modal without saving changes
   */
  const handlePeriodCancel = useCallback(() => {
    setPeriodModalVisible(false);
    setTempHours([]);
    setSelectedPeriod(null); // Reset selectedPeriod to ensure CalendarEventDetails is hidden
  }, []);

  /**
   * Updates the temporary hours when the calendar picker changes
   *
   * @param {Array} newHours - New array of hour objects from the calendar picker
   */
  const handleHoursChange = useCallback(
    (newHours) => {
      // Make sure we have an array of hours
      const updatedHours = Array.isArray(newHours) ? newHours : [];

      // Store the hours and ensure they have the proper format
      setTempHours(
        updatedHours.map((hour) => ({
          ...hour,
          // employee_id: selectedPeriod?.employee_id,
        }))
      );

      // Also update the selectedPeriod to keep it in sync
      setSelectedPeriod((prev) => ({
        ...prev,
        non_available_hours: updatedHours,
      }));
    },
    [selectedPeriod?.employee_id]
  );

  /**
   * Saves the period with all changes after form validation
   */
  const handlePeriodSave = useCallback(() => {
    modalForm.validateFields().then((values) => {
      // Get the current selectedPeriod value directly from state to ensure it's the latest
      const latestPeriod = selectedPeriod;

      if (!latestPeriod) {
        console.error("selectedPeriod is undefined on save");
        return;
      }

      // Make sure tempHours is properly formatted
      const formattedHours = tempHours.map((hour) => ({
        ...hour,
        // employee_id: latestPeriod.employee_id,
      }));

      // Create a fresh copy of the period object with updated hours
      const newPeriod = {
        ...latestPeriod,
        non_available_hours: formattedHours,
        effective_from:
          values.effective_from &&
          typeof values.effective_from.toDate === "function"
            ? values.effective_from.toDate().toISOString()
            : values.effective_from instanceof Date
            ? values.effective_from.toISOString()
            : values.effective_from,
        effective_to: values.effective_to
          ? typeof values.effective_to.toDate === "function"
            ? values.effective_to.toDate().toISOString()
            : values.effective_to instanceof Date
            ? values.effective_to.toISOString()
            : values.effective_to
          : null,
      };

      const periodExists = localPeriods.some((p) => p.id === latestPeriod.id);

      // Update local state
      let updatedPeriods;
      if (periodExists) {
        updatedPeriods = localPeriods.map((p) =>
          p.id === latestPeriod.id ? newPeriod : p
        );
      } else {
        updatedPeriods = [...localPeriods, newPeriod];
      }

      // Update local state first
      setLocalPeriods(updatedPeriods);

      // Then update form value
      onChange(updatedPeriods);

      // Close modal and clean up
      setPeriodModalVisible(false);
      setTempHours([]);
      setSelectedPeriod(null); // Reset selectedPeriod to ensure CalendarEventDetails is hidden
    });
  }, [modalForm, selectedPeriod, localPeriods, tempHours, onChange]);

  /**
   * Checks if there's an open period (period with start date but no end date)
   * @returns {boolean} True if an open period exists
   */
  const hasOpenPeriod = useCallback(() => {
    return (
      localPeriods?.some(
        (period) => period.effective_from && !period.effective_to
      ) || false
    );
  }, [localPeriods]);

  // Memoize columns to prevent unnecessary re-renders
  const columns = useMemo(
    () => [
      {
        title: (
          <FormattedMessage id="app.forms.employee.nonAvailablePeriods.effectiveFrom" />
        ),
        dataIndex: "effective_from",
        key: "effective_from",
        render: (date) => moment(date).format("L"),
      },
      {
        title: (
          <FormattedMessage id="app.forms.employee.nonAvailablePeriods.effectiveTo" />
        ),
        dataIndex: "effective_to",
        key: "effective_to",
        render: (date) =>
          date ? (
            moment(date).format("L")
          ) : (
            <FormattedMessage id="app.forms.employee.nonAvailablePeriods.ongoing" />
          ),
      },
      {
        title: (
          <FormattedMessage id="app.forms.employee.nonAvailablePeriods.daysCount" />
        ),
        dataIndex: "non_available_hours",
        key: "days_count",
        render: (hours) => calculateDaysCount(hours),
      },
      {
        title: (
          <FormattedMessage id="app.forms.employee.nonAvailablePeriods.totalHours" />
        ),
        dataIndex: "non_available_hours",
        key: "total_hours",
        render: (hours) => (
          <span>
            {calculateTotalHours(hours)}{" "}
            <FormattedMessage id="app.forms.employee.nonAvailablePeriods.hours" />
          </span>
        ),
      },
      {
        title: (
          <FormattedMessage id="app.forms.employee.nonAvailablePeriods.actions" />
        ),
        key: "actions",
        render: (_, record) => (
          <Space>
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => handleEditPeriod(record)}
            />
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDeletePeriod(record.id)}
            />
          </Space>
        ),
      },
    ],
    [
      calculateDaysCount,
      calculateTotalHours,
      handleEditPeriod,
      handleDeletePeriod,
    ]
  );

  // Memoize the entityId to prevent unnecessary re-renders
  const entityId = useMemo(
    () => selectedPeriod?.employee_id,
    [selectedPeriod?.employee_id]
  );

  return (
    <>
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginBottom: 16,
        }}
      >
        <Tooltip
          title={
            hasOpenPeriod() ? (
              <FormattedMessage id="app.forms.employee.nonAvailablePeriods.openPeriodWarning" />
            ) : null
          }
        >
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAddPeriod}
            disabled={hasOpenPeriod()}
          >
            <FormattedMessage id="app.forms.employee.nonAvailablePeriods.addPeriod" />
          </Button>
        </Tooltip>
      </div>

      <Table
        columns={columns}
        dataSource={localPeriods || []}
        rowKey="id"
        locale={{
          emptyText: <ChildImg />,
        }}
      />

      <Modal
        title={
          <FormattedMessage
            id={
              selectedPeriod?.non_available_hours?.length > 0
                ? "app.forms.employee.nonAvailablePeriods.editPeriod"
                : "app.forms.employee.nonAvailablePeriods.addPeriod"
            }
          />
        }
        open={periodModalVisible}
        onCancel={handlePeriodCancel}
        width="90vw"
        style={{
          height: "96vh",
          maxWidth: "90vw",
          top: 20,
        }}
        styles={{
          body: {
            height: "100%",
            width: "100%",
            padding: "40px 0 0 0",
          },
        }}
        destroyOnClose={false}
        forceRender={true}
        footer={null} // Remove footer buttons
      >
        {/* Modal action buttons and date selection in the same row */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 16,
          }}
        >
          {/* Date picker form on the left */}
          <Form
            form={modalForm}
            layout="horizontal"
            style={{ marginBottom: 0 }}
          >
            <Row gutter={16} style={{ flexWrap: "nowrap" }}>
              <Col style={{ minWidth: "220px" }}>
                <DatePickerItem
                  name="effective_from"
                  category={"employee.nonAvailablePeriods"}
                  required={true}
                  disabledDate={(current) =>
                    disableStartDatesFunc(
                      current,
                      localPeriods,
                      selectedPeriod,
                      modalForm
                    )
                  }
                />
              </Col>
              <Col style={{ minWidth: "220px" }}>
                <DatePickerItem
                  name="effective_to"
                  category={"employee.nonAvailablePeriods"}
                  required={false}
                  disabledDate={(current) =>
                    disableEndDatesFunc(
                      current,
                      localPeriods,
                      selectedPeriod,
                      modalForm
                    )
                  }
                />
              </Col>
            </Row>
          </Form>

          {/* Action buttons on the right */}
          <div style={{ display: "flex", gap: "8px" }}>
            <Button type="primary" onClick={handlePeriodSave}>
              <FormattedMessage id="app.actions.save" />
            </Button>
            <Button onClick={handlePeriodCancel}>
              <FormattedMessage id="app.actions.cancel" />
            </Button>
          </div>
        </div>

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 16,
            maxHeight: "calc(100% - 50px)",
            overflowY: "auto",
          }}
        >
          {/* Calendar picker for non-available hours */}
          <div>
            {periodModalVisible && selectedPeriod && (
              <div
                style={{
                  border: "1px solid #f0f0f0",
                  borderRadius: 4,
                  padding: 16,
                }}
              >
                <PeriodsCalenderPicker
                  title={
                    <FormattedMessage id="app.forms.employee.nonAvailablePeriods.nonAvailableHours" />
                  }
                  comp={<CalendarEntryComponent />}
                  record={tempHours}
                  onChange={handleHoursChange}
                  entityId={entityId}
                />
              </div>
            )}
          </div>
        </div>
      </Modal>
    </>
  );
};

// Memoize the entire PeriodsTable component to prevent unnecessary re-renders
const MemoizedPeriodsTable = React.memo(PeriodsTable);

/**
 * AvailabilityPeriods component - Manages employee availability periods
 * This component allows adding, editing and removing non-available periods
 * for employees, including specific hours and days they cannot work.
 *
 * @component
 * @param {Object} props - Component props
 * @param {Object} props.activeEmployee - The employee object whose availability is being managed
 * @param {React.Ref} ref - Forwarded ref for DOM access
 * @returns {React.ReactElement} The availability periods management form
 */
const AvailabilityPeriods = forwardRef((props, ref) => {
  const { activeEmployee } = props;
  const form = Form.useFormInstance();

  // Initialize the form with employee data when it changes
  useEffect(() => {
    if (activeEmployee?.non_available_periods) {
      form.setFieldsValue({
        non_available_periods: activeEmployee.non_available_periods,
      });
    }
  }, [activeEmployee, form]);

  return (
    <div style={{ display: "flex", flexDirection: "column" }} ref={ref}>
      <Surface>
        <FormHeader
          title={<FormattedMessage id="app.employees.availability" />}
        />

        <Form.Item
          name="non_available_periods"
          initialValue={activeEmployee?.non_available_periods || []}
        >
          <MemoizedPeriodsTable />
        </Form.Item>
      </Surface>
    </div>
  );
});

export default React.memo(AvailabilityPeriods);
